import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, X } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Radix for anything with a popup, so ARIA comes correct by construction and
 * focus is trapped and returned without hand-rolled logic (§10.9).
 *
 * Motion is driven by Radix `data-state` attributes in CSS — Framer is
 * reserved for the three cases that genuinely need it (§10.7).
 */

// ── Modal ───────────────────────────────────────────────────────────────

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = 'md',
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  width?: 'sm' | 'md' | 'lg';
}) {
  const widths = { sm: 'max-w-[26rem]', md: 'max-w-[34rem]', lg: 'max-w-[48rem]' };
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-ink-900/45 backdrop-blur-[2px]',
            'data-[state=open]:animate-[fade-rise_160ms_var(--ease-out)]',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
            widths[width],
            'card overflow-hidden shadow-pop',
            // Centered scale+fade that keeps the -50%/-50% translate baked into
            // the keyframe, so the dialog grows from the middle instead of
            // sliding in from a corner. (See .modal-anim in styles/index.css.)
            'modal-anim',
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-line px-6 py-4">
            <div>
              <DialogPrimitive.Title className="font-display text-section text-strong">
                {title}
              </DialogPrimitive.Title>
              {description && (
                <DialogPrimitive.Description className="mt-1 text-ui text-muted">
                  {description}
                </DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close
              aria-label="Yopish"
              className="-mr-1 -mt-1 cursor-pointer rounded-control p-2 text-muted transition-ui duration-instant hover:bg-raised hover:text-strong"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>
          {children && <div className="max-h-[65vh] overflow-y-auto px-6 py-5">{children}</div>}
          {footer && (
            <div className="flex items-center justify-end gap-2 border-t border-line bg-raised px-6 py-4">
              {footer}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ── Drawer (right side) ─────────────────────────────────────────────────

export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-ink-900/45 backdrop-blur-[2px] data-[state=open]:animate-[fade-rise_160ms_var(--ease-out)]" />
        <DialogPrimitive.Content
          className={cn(
            'fixed right-0 top-0 z-50 flex h-full w-full max-w-[32rem] flex-col border-l border-line bg-surface shadow-pop',
            'data-[state=open]:animate-[fade-rise_220ms_var(--ease-out)]',
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-line px-6 py-4">
            <div>
              <DialogPrimitive.Title className="font-display text-section text-strong">{title}</DialogPrimitive.Title>
              {description && (
                <DialogPrimitive.Description className="mt-1 text-ui text-muted">{description}</DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close
              aria-label="Yopish"
              className="-mr-1 -mt-1 cursor-pointer rounded-control p-2 text-muted transition-ui duration-instant hover:bg-raised hover:text-strong"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
          {footer && <div className="flex items-center justify-end gap-2 border-t border-line bg-raised px-6 py-4">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ── Tooltip ─────────────────────────────────────────────────────────────

export const TooltipProvider = TooltipPrimitive.Provider;

/**
 * NOTE: the trigger renders `asChild`, and Radix writes its own `data-state`
 * (open / delayed-open / closed) onto that element. Do NOT wrap a component
 * that styles itself off `data-state` — Switch, Select, Accordion — directly:
 * the tooltip's state wins and the control renders permanently "off". Wrap it
 * in a plain <span> instead.
 */
export function Tooltip({ label, children, side = 'top' }: { label: string; children: ReactNode; side?: 'top' | 'right' | 'bottom' | 'left' }) {
  return (
    <TooltipPrimitive.Root delayDuration={350}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          className={cn(
            'z-50 max-w-[18rem] rounded-control bg-ink-700 px-2.5 py-1.5 text-ui text-white shadow-pop dark:bg-ink-500',
            'data-[state=delayed-open]:animate-[fade-rise_160ms_var(--ease-out)]',
          )}
        >
          {label}
          <TooltipPrimitive.Arrow className="fill-ink-700 dark:fill-ink-500" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

// ── Switch ──────────────────────────────────────────────────────────────

/**
 * forwardRef and prop spread are not ceremony here: Radix's Tooltip and
 * Popover triggers use `asChild`, which clones the child and hands it a ref
 * plus event handlers. A plain function component silently drops both — the
 * tooltip then never opens and React logs a ref warning.
 */
export const Switch = forwardRef<
  HTMLButtonElement,
  ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>
>(function Switch({ className, ...props }, ref) {
  return (
    <SwitchPrimitive.Root
      ref={ref}
      className={cn(
        'relative h-6 w-11 shrink-0 cursor-pointer rounded-chip border border-line transition-ui duration-instant',
        'data-[state=checked]:border-brand-500 data-[state=checked]:bg-brand-500 data-[state=unchecked]:bg-raised',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'block h-4 w-4 translate-x-1 rounded-chip bg-white shadow-[0_1px_2px_rgb(4_6_26_/_0.25)] transition-transform duration-quick ease-out',
          'data-[state=checked]:translate-x-[1.4rem]',
        )}
      />
    </SwitchPrimitive.Root>
  );
});

// ── Select ──────────────────────────────────────────────────────────────

export function Select<T extends string>({
  value,
  onValueChange,
  options,
  placeholder,
  id,
  className,
}: {
  value: T | '';
  onValueChange: (value: T) => void;
  options: Array<{ value: T; label: string; hint?: string }>;
  placeholder?: string;
  id?: string;
  className?: string;
}) {
  return (
    <SelectPrimitive.Root value={value || undefined} onValueChange={(v) => onValueChange(v as T)}>
      <SelectPrimitive.Trigger
        id={id}
        className={cn(
          'flex h-10 w-full cursor-pointer items-center justify-between gap-2 rounded-control border border-line bg-surface px-3 text-body text-strong',
          'transition-ui duration-instant hover:border-line-strong data-[state=open]:border-brand-500 dark:hover:border-brand-500/60',
          className,
        )}
      >
        <SelectPrimitive.Value placeholder={<span className="text-faint">{placeholder}</span>} />
        <SelectPrimitive.Icon>
          <ChevronDown className="h-4 w-4 text-muted" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className={cn(
            'z-50 max-h-[20rem] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-card border border-line bg-surface shadow-pop',
            'data-[state=open]:animate-[fade-rise_160ms_var(--ease-out)]',
          )}
        >
          <SelectPrimitive.Viewport className="p-1">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                className={cn(
                  'relative flex cursor-pointer select-none flex-col gap-0.5 rounded-control px-3 py-2 pr-8 text-ui text-strong outline-none',
                  'data-[highlighted]:bg-raised data-[state=checked]:text-brand-600 dark:data-[state=checked]:text-brand-300',
                )}
              >
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                {option.hint && <span className="text-code text-muted">{option.hint}</span>}
                <SelectPrimitive.ItemIndicator className="absolute right-2 top-2.5">
                  <Check className="h-4 w-4" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
